import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import SEO from '@/components/SEO';

const EFFECTIVE_DATE = 'May 19, 2026';
const PRIVACY_EMAIL = 'privacy@opencardvault.com';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-3">{title}</h2>
      <div className="text-gray-700 dark:text-zinc-300 space-y-3 leading-relaxed">{children}</div>
    </section>
  );
}

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950">
      <SEO
        title="Privacy Policy"
        description="Read Open Card Vault's privacy policy. Learn how we collect, use, and protect your personal information."
        url="/legal/privacy"
      />
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-12">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-7 w-7" />
            <h1 className="text-3xl font-display font-bold">Privacy Policy</h1>
          </div>
          <p className="text-brand-100">Effective date: {EFFECTIVE_DATE}</p>
        </div>
      </div>

      <div className="container-custom py-10 max-w-3xl">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-8">

          <p className="text-gray-700 dark:text-zinc-300 mb-8 leading-relaxed">
            Open Card Vault ("we," "our," or "us") operates the Open Card Vault trading card
            marketplace and related services (the "Service"). This Privacy Policy explains how we
            collect, use, disclose, retain, and protect personal information when you access or
            use the Service. By using Open Card Vault, you acknowledge the practices described in
            this Privacy Policy.
          </p>

          <Section title="1. Age Requirements">
            <p>Open Card Vault is available only to users who are at least 13 years of age.</p>
            <p>
              Users between the ages of 13 and 17 may create an account and use certain
              non-transactional features of the Service, including browsing cards and listings,
              maintaining card collections, creating decks and wishlists, viewing publicly available
              marketplace information, and using other features made available to minor users.
            </p>
            <p>Users under 18 years of age are prohibited from participating in marketplace transactions. This includes, but is not limited to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Buying or selling cards, merchandise, sealed products, or other items</li>
              <li>Placing bids</li>
              <li>Creating marketplace listings</li>
              <li>Making or accepting purchase offers</li>
              <li>Receiving payments or payouts</li>
              <li>Creating or operating seller accounts</li>
              <li>Connecting payment or payout accounts</li>
              <li>Entering into transactions with other users through the Service</li>
            </ul>
            <p>
              Marketplace transaction features are available only to users who are at least 18 years
              of age and who satisfy any identity verification, payment processor, or other
              eligibility requirements imposed by Open Card Vault or its third-party service
              providers.
            </p>
            <p>
              We may request a user's date of birth, age confirmation, identity verification, or
              other information reasonably necessary to determine eligibility for age-restricted
              features.
            </p>
            <p>
              Providing false age information, using another person's identity, or attempting to
              circumvent age restrictions may result in suspension or termination of the account.
            </p>
            <p>
              Open Card Vault does not knowingly permit children under 13 years of age to create
              accounts or use the Service. If we learn that we have collected personal information
              from a child under 13, we will take reasonable steps to delete the information and
              terminate the associated account.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>We may collect the following categories of information.</p>
            <p>
              <strong>Account information</strong> — When you register, we collect information such
              as your email address, username, password in hashed form, date of birth or age
              confirmation, and, when voluntarily provided, your first and last name.
            </p>
            <p>
              <strong>Profile information</strong> — We collect profile photos, biography
              information, and other information you voluntarily add to your public profile.
            </p>
            <p>
              <strong>Age and eligibility information</strong> — We may collect your date of birth,
              age confirmation, identity verification status, or similar information to determine
              whether you are eligible to access age-restricted marketplace and payment features.
            </p>
            <p>
              <strong>Marketplace activity</strong> — We collect information concerning listings you
              create, purchases, bids, offers, transactions, cancellations, disputes, and
              transaction history.
            </p>
            <p>
              <strong>Messages and communications</strong> — We collect and store messages and other
              communications you send through the Service. We may access, review, or disclose
              communications when reasonably necessary to investigate reports, enforce our Terms of
              Service, protect users, prevent fraud or abuse, comply with legal obligations, or
              maintain the security and integrity of the Service.
            </p>
            <p>
              <strong>Uploaded content</strong> — We collect photographs and other content you
              upload for marketplace listings, profile pictures, collections, wishlists, decks, or
              other Service features.
            </p>
            <p>
              <strong>Collection and activity data</strong> — We collect information concerning
              cards you add to your collection, deck builds, wishlists, set completion information,
              achievements, experience points, trophies, and other activity associated with your
              account.
            </p>
            <p>
              <strong>Technical information</strong> — We may automatically collect information such
              as your IP address, browser type, device information, operating system, pages visited,
              referring pages, approximate geographic location derived from IP address, timestamps,
              diagnostic information, and interaction logs.
            </p>
            <p>
              <strong>Payment and transaction information</strong> — Payment card information is
              processed by our third-party payment processor. Open Card Vault does not directly
              store your complete payment card number. We may receive and store transaction
              identifiers, payment status, payout status, payment processor account identifiers,
              dispute information, and other information necessary to operate the marketplace.
            </p>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>We may use personal information to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide, operate, maintain, and improve the Service</li>
              <li>Create and manage user accounts</li>
              <li>Determine eligibility for age-restricted features</li>
              <li>Operate card collections, decks, wishlists, marketplace listings, auctions, offers, reviews, achievements, trophies, and other Service features</li>
              <li>Process marketplace transactions and facilitate payments and payouts</li>
              <li>Provide customer support</li>
              <li>Send transactional and administrative communications</li>
              <li>Send marketplace notifications, including order confirmations, auction results, offers, messages, security alerts, and account notifications</li>
              <li>Send marketing communications where permitted by law — you may unsubscribe at any time</li>
              <li>Detect, investigate, and prevent fraud, abuse, harassment, illegal activity, security incidents, and violations of our Terms of Service</li>
              <li>Moderate content and enforce our policies</li>
              <li>Protect the rights, safety, property, and security of Open Card Vault, our users, and others</li>
              <li>Comply with legal and regulatory obligations</li>
              <li>Measure Service performance and analyze usage patterns</li>
              <li>Display advertisements and measure advertising performance where permitted by applicable law and our advertising partners' policies</li>
            </ul>
          </Section>

          <Section title="4. Cookies and Similar Technologies">
            <p>
              We use cookies and similar technologies to operate and secure the Service, maintain
              login sessions, remember preferences, analyze Service usage, prevent fraud, and
              support advertising.
            </p>
            <p>
              <strong>Essential cookies</strong> are necessary for core Service functionality,
              security, authentication, fraud prevention, and user preferences.
            </p>
            <p>
              <strong>Analytics technologies</strong> may be used to understand how users interact
              with the Service and improve platform performance. Where required by applicable law,
              analytics technologies will not be activated until the user provides the required
              consent.
            </p>
            <p>
              <strong>Advertising technologies</strong> may be used by Open Card Vault and our
              advertising partners to display, measure, and manage advertisements. Where required
              by applicable law or advertising platform policies, advertising technologies will not
              be activated until the required consent has been obtained.
            </p>
            <p>
              Users may manage applicable cookie preferences through our cookie consent tools and
              browser privacy settings. Declining non-essential cookies may prevent analytics or
              advertising technologies from loading but will not prevent access to essential Service
              functionality.
            </p>
          </Section>

          <Section title="5. Third-Party Services">
            <p>
              We use third-party service providers to operate the Service. These providers may
              process personal information on our behalf or independently under their own privacy
              policies. Service providers may include:
            </p>
            <p>
              <strong>Stripe</strong> — Payment processing, seller onboarding, identity
              verification, fraud prevention, transaction processing, and payouts.
            </p>
            <p>
              <strong>Amazon Web Services (AWS)</strong> — Cloud infrastructure, application
              hosting, storage, databases, content delivery, logging, and other infrastructure
              services.
            </p>
            <p>
              <strong>Cloudflare R2</strong> — File and image storage and delivery, if enabled or
              used by the Service.
            </p>
            <p>
              <strong>Resend or SendGrid</strong> — Transactional, administrative, and
              notification email delivery, depending on the email provider used by the Service.
            </p>
            <p>
              <strong>Google Analytics</strong> — Website and application analytics, if enabled.
            </p>
            <p>
              <strong>Google AdSense and related Google advertising services</strong> — Advertising
              delivery, measurement, fraud prevention, and related advertising services, if enabled.
            </p>
            <p>
              We disclose personal information to service providers only as reasonably necessary to
              operate, secure, maintain, and improve the Service or comply with legal obligations.
              We do not sell personal information to third parties for their own independent
              marketing purposes.
            </p>
          </Section>

          <Section title="6. Advertising">
            <p>
              Open Card Vault may display advertisements provided by Google AdSense or other
              advertising networks. Advertising providers may use cookies, device identifiers, IP
              addresses, and similar technologies to deliver, measure, limit, and protect
              advertisements.
            </p>
            <p>
              Personalized advertising may be restricted based on a user's age, location, consent
              choices, applicable law, and the requirements of our advertising partners. Open Card
              Vault does not knowingly enable personalized advertising for users where doing so
              would violate applicable law or advertising platform requirements.
            </p>
            <p>
              Users may manage advertising preferences through our cookie consent tools, browser
              privacy controls, and advertising preference tools provided by applicable advertising
              providers.
            </p>
          </Section>

          <Section title="7. Users Under 18">
            <p>
              Open Card Vault permits users who are at least 13 years of age to create accounts and
              use certain non-transactional Service features. Users under 18 are prohibited from
              participating in marketplace transactions, creating marketplace listings, buying or
              selling items, placing bids, making or accepting purchase offers, receiving payments
              or payouts, creating seller accounts, connecting payment accounts, or otherwise
              entering into transactions through the Service.
            </p>
            <p>
              Open Card Vault may restrict advertising personalization, communications, messaging
              features, data collection, profile visibility, or other functionality for users under
              18 when reasonably necessary to comply with applicable law, advertising platform
              requirements, payment processor requirements, or our safety policies.
            </p>
            <p>
              We may implement additional safeguards for users under 18, including restrictions on
              communications, public profile information, location information, content visibility,
              and interactions with other users.
            </p>
            <p>
              If we learn that a user under 13 has created an account or provided personal
              information to Open Card Vault, we will take reasonable steps to delete the
              information and terminate the associated account.
            </p>
          </Section>

          <Section title="8. How We Share Information">
            <p>We may disclose personal information:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>To service providers that operate infrastructure, payments, email delivery, analytics, advertising, security, customer support, or other services on our behalf</li>
              <li>To other users when information is intentionally made public through profiles, listings, reviews, marketplace activity, or other public Service features</li>
              <li>When necessary to process marketplace transactions</li>
              <li>When reasonably necessary to investigate fraud, abuse, harassment, security incidents, or violations of our policies</li>
              <li>When required by law, regulation, subpoena, court order, or other valid legal process</li>
              <li>To protect the rights, safety, property, or security of Open Card Vault, our users, or others</li>
              <li>In connection with a merger, acquisition, financing, sale of assets, bankruptcy, or other business transaction involving Open Card Vault</li>
            </ul>
            <p>We do not sell personal information to third parties for their independent marketing purposes.</p>
          </Section>

          <Section title="9. Data Retention">
            <p>
              We retain personal information for as long as reasonably necessary to operate the
              Service, provide requested functionality, maintain security, resolve disputes, enforce
              agreements, and comply with legal obligations. Account information is generally
              retained while an account remains active.
            </p>
            <p>
              When a user requests account deletion, we will delete or anonymize personal
              information within a reasonable period, except where retention is necessary for
              legal, security, fraud prevention, dispute resolution, financial reporting, or other
              legitimate purposes. Marketplace transaction records may be retained for up to seven
              years or for another period required by applicable law. Backups and system logs may
              retain information for a limited period after account deletion before being
              automatically deleted or overwritten.
            </p>
          </Section>

          <Section title="10. Your Privacy Rights">
            <p>
              Depending on where you live, you may have certain rights regarding your personal
              information. These rights may include:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Requesting access to personal information we maintain about you</li>
              <li>Requesting correction of inaccurate personal information</li>
              <li>Requesting deletion of certain personal information</li>
              <li>Requesting a portable copy of certain personal information</li>
              <li>Objecting to or restricting certain processing activities</li>
              <li>Withdrawing consent where processing is based on consent</li>
              <li>Opting out of certain targeted advertising or data-sharing activities where required by applicable law</li>
              <li>Appealing certain decisions concerning privacy requests where required by applicable law</li>
            </ul>
            <p>
              We may need to verify your identity before completing certain requests. To submit a
              privacy request, contact us at{' '}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="text-brand-600 dark:text-brand-400 hover:underline">
                {PRIVACY_EMAIL}
              </a>.
              We will respond within the time required by applicable law.
            </p>
          </Section>

          <Section title="11. Account Deletion">
            <p>
              Users may request deletion of their Open Card Vault account through the account
              settings provided by the Service or by contacting us at{' '}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="text-brand-600 dark:text-brand-400 hover:underline">
                {PRIVACY_EMAIL}
              </a>.
            </p>
            <p>
              Deleting an account may permanently remove access to collections, decks, wishlists,
              messages, listings, achievements, and other account information. Certain information
              may be retained when reasonably necessary to comply with legal obligations, maintain
              transaction records, prevent fraud, resolve disputes, enforce agreements, or protect
              the security of the Service.
            </p>
          </Section>

          <Section title="12. Data Security">
            <p>
              We implement reasonable administrative, technical, and organizational safeguards
              designed to protect personal information. These safeguards may include HTTPS
              encryption, encryption of certain stored data, password hashing, access controls,
              authentication controls, network security measures, monitoring, logging, backups, and
              restrictions on employee or administrator access.
            </p>
            <p>
              No method of electronic transmission or storage is completely secure, and we cannot
              guarantee absolute security. Users are responsible for maintaining the confidentiality
              of their account credentials and should use strong, unique passwords.
            </p>
          </Section>

          <Section title="13. International Users">
            <p>
              Open Card Vault is operated from the United States. If you access the Service from
              outside the United States, your information may be transferred to, stored in, or
              processed in the United States and other countries where our service providers
              operate. Data protection laws in these jurisdictions may differ from the laws of your
              country of residence. Where required, we will implement appropriate safeguards for
              international transfers of personal information.
            </p>
          </Section>

          <Section title="14. Changes to This Privacy Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make material changes,
              we may notify users through email, an in-app notification, a notice displayed through
              the Service, or other reasonable methods. The updated Privacy Policy will become
              effective on the date identified at the beginning of the Policy. Your continued use
              of the Service after the effective date of an updated Privacy Policy constitutes
              acknowledgment of the updated Policy to the extent permitted by applicable law.
            </p>
          </Section>

          <Section title="15. Contact Us">
            <p>For privacy-related questions, concerns, or requests, contact:</p>
            <p>
              <strong>Open Card Vault</strong><br />
              Email:{' '}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="text-brand-600 dark:text-brand-400 hover:underline">
                {PRIVACY_EMAIL}
              </a>
            </p>
          </Section>

          <div className="border-t border-gray-200 dark:border-zinc-800 pt-6 mt-8 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-zinc-500">
            <Link to="/legal/terms" className="hover:text-brand-600 dark:hover:text-brand-400">Terms of Service</Link>
            <Link to="/legal/community-guidelines" className="hover:text-brand-600 dark:hover:text-brand-400">Community Guidelines</Link>
            <Link to="/guide" className="hover:text-brand-600 dark:hover:text-brand-400">How It Works</Link>
            <Link to="/contact" className="hover:text-brand-600 dark:hover:text-brand-400">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
